import { useState } from 'react';
import { useBusinessData } from '../../authentication_components/businessDataContext';
import { useBusinessAccess } from '../../authentication_components/businessAccessContext';
import { useAdminTracker } from '../../authentication_components/adminTrackerContext';
import { useAuth } from '../../authentication_components/authContext';
import { Search, AlertTriangle, Building, User, Globe, Package, Wrench, Users, PartyPopper, QrCode, X, Loader2, PlusCircle, Crown, UserCog, MapPin } from 'lucide-react';
import { toast } from 'react-hot-toast';

// A stateless component for the toggle switch UI
const ToggleSwitch = ({ isEnabled, onChange, disabled = false }) => (
  <button
    onClick={onChange}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
      isEnabled ? 'bg-green-500' : 'bg-gray-300'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        isEnabled ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

export default function ManageAccess() {
  // State for user input and search status
  const [registryNumber, setRegistryNumber] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // State for fetched data
  const [businessData, setBusinessData] = useState(null);
  const [accessControls, setAccessControls] = useState([]);
  const [masterAccess, setMasterAccess] = useState([]);
  const [adminAccess, setAdminAccess] = useState([]);

  // State for UI interactions
  const [isToggling, setIsToggling] = useState(null); 
  const [isCreatingDefault, setIsCreatingDefault] = useState(false);
  const [isCreatingMaster, setIsCreatingMaster] = useState(false);
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  
  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);

  // Hooks for data fetching
  const { getBusinessByRegistryNumber } = useBusinessData();
  const { 
    getAccessControls, 
    toggleAccessPermission, 
    toggleMasterAccessPermission,
    createDefaultAccess,
    createDefaultMasterAccess,
    separateAccessRecords,
    isMasterAccessRecord,
    getAccessRecordDisplayName,
    isMasterUser,
    isAdminUser
  } = useBusinessAccess();

  // Admin Tracker Hooks
  const { createTracker } = useAdminTracker();
  const { adminId: currentAdminId } = useAuth();

  // Configuration for displaying access permissions
  const accessConfig = {
    public_access:   { label: 'ACCESO PÚBLICO', icon: <Globe className="h-5 w-5" /> },
    product_access:  { label: 'PRODUCTOS', icon: <Package className="h-5 w-5" /> },
    service_access:  { label: 'SERVICIOS', icon: <Wrench className="h-5 w-5" /> },
    employee_access: { label: 'EMPLEADOS', icon: <Users className="h-5 w-5" /> },
    event_access:    { label: 'EVENTOS', icon: <PartyPopper className="h-5 w-5" /> },
    qr_access:       { label: 'CÓDIGOS QR', icon: <QrCode className="h-5 w-5" /> },
    location_access: { label: 'LOCALIZACIONES', icon: <MapPin classNamme="h-5 w-5"/> },
  };

  // Main search function
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!registryNumber.trim()) return;
    
    setIsSearching(true);
    setSearchError(null);
    setBusinessData(null);
    setAccessControls([]);
    setMasterAccess([]);
    setAdminAccess([]);
    
    try {
      const business = await getBusinessByRegistryNumber(registryNumber.trim());
      setBusinessData(business);

      const accessData = await getAccessControls(business.business_registry_number);
      const controls = accessData.access_controls || [];
      setAccessControls(controls);

      const { masterAccess: master, adminAccess: admin } = separateAccessRecords(controls);
      setMasterAccess(master);
      setAdminAccess(admin);
    } catch (err) {
      setSearchError(err.message || "An unexpected error occurred.");
      setBusinessData(null);
      setAccessControls([]);
      setMasterAccess([]);
      setAdminAccess([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Function to create default permissions for the current admin
  const handleCreateDefaultAccess = async () => {
    if (!registryNumber) return;
    setIsCreatingDefault(true);
    try {
      await createDefaultAccess(registryNumber, false); 
      toast.success('Default access created for you successfully!');
      
      try {
        await createTracker({
          admin_id: currentAdminId || 'unknown',
          change_type: 'access_control_create',
          change_reason: `Business ${registryNumber} (${businessData?.business_name}) - Initialized ADMIN access controls for business: ${registryNumber}`
        });
      } catch (e) { console.error('Tracking failed', e); }

      await refreshAccessControls();
    } catch (err) {
      toast.error(err.message || 'Failed to create default access.');
    } finally {
      setIsCreatingDefault(false);
      setShowCreateOptions(false);
    }
  };

  // Function to create master access (only for master users)
  const handleCreateMasterAccess = async () => {
    if (!registryNumber || !isMasterUser) return;
    setIsCreatingMaster(true);
    try {
      await createDefaultMasterAccess(registryNumber);
      toast.success('Master access created successfully!');

      try {
        await createTracker({
          admin_id: currentAdminId || 'unknown',
          change_type: 'access_control_create_master',
          change_reason: `Business ${registryNumber} (${businessData?.business_name}) - Initialized MASTER access controls for business: ${registryNumber}`
        });
      } catch (e) { console.error('Tracking failed', e); }

      await refreshAccessControls();
    } catch (err) {
      toast.error(err.message || 'Failed to create master access.');
    } finally {
      setIsCreatingMaster(false);
      setShowCreateOptions(false);
    }
  };

  const refreshAccessControls = async () => {
    try {
      const accessData = await getAccessControls(registryNumber);
      const controls = accessData.access_controls || [];
      setAccessControls(controls);

      const { masterAccess: master, adminAccess: admin } = separateAccessRecords(controls);
      setMasterAccess(master);
      setAdminAccess(admin);
    } catch (err) {
      console.error('Failed to refresh access controls:', err);
    }
  };

  // Prepares the toggle action, showing a confirmation dialog if disabling
  const handleAccessToggle = (control, accessType, currentValue) => {
    const isMaster = isMasterAccessRecord(control);
    const displayName = getAccessRecordDisplayName(control);
    
    const change = {
      control,
      accessType,
      newValue: !currentValue,
      accessName: accessConfig[accessType].label,
      userDisplayName: displayName,
      isMaster
    };

    if (currentValue === true) { 
      setPendingChange(change);
      setShowConfirmDialog(true);
    } else { 
      executeToggle(change);
    }
  };

  // Executes the API call to toggle a permission
  const executeToggle = async (change) => {
    const { control, accessType, newValue, isMaster, accessName, userDisplayName } = change;
    const toggleKey = isMaster ? `master-${accessType}` : `${control.admin_id}-${accessType}`;
    setIsToggling(toggleKey);
    
    try {
      // 1. Perform Action
      if (isMaster) {
        await toggleMasterAccessPermission(registryNumber, accessType, newValue);
        toast.success(`Master access for ${accessName} updated.`);
      } else {
        await toggleAccessPermission(registryNumber, control.admin_id, accessType, newValue);
        toast.success(`Access for ${accessName} updated for ${control.admin_id}.`);
      }

      // 2. TRACK ACTION - UPDATED LOGIC
      try {
        await createTracker({
          admin_id: currentAdminId || 'unknown',
          change_type: isMaster ? 'master_access_toggle' : 'admin_access_toggle',
          // UPDATED: Logs "for business: [Registry Number]" instead of "for Admin: [ID]"
          change_reason: `Business ${registryNumber} (${businessData?.business_name}) - ${isMaster ? 'MASTER' : 'ADMIN'} Access: Changed ${accessName} to ${newValue ? 'ENABLED' : 'DISABLED'} for business: ${registryNumber} (Target ID: ${userDisplayName})`
        });
        console.log('[AdminTracker] Access toggle tracked successfully');
      } catch (trackerErr) {
        console.error('[AdminTracker] Failed to track access toggle:', trackerErr);
      }
      
      await refreshAccessControls();
    } catch (err) {
      toast.error(err.message || 'Failed to update access.');
    } finally {
      setIsToggling(null);
      setShowConfirmDialog(false);
      setPendingChange(null);
    }
  };

  const confirmAccessChange = () => {
    if (pendingChange) {
      executeToggle(pendingChange);
    }
  };

  const cancelAccessChange = () => {
    setShowConfirmDialog(false);
    setPendingChange(null);
  };

  const renderAccessControlCard = (control, cardType = 'admin') => {
    const isMaster = isMasterAccessRecord(control);
    const displayName = getAccessRecordDisplayName(control);
    
    return (
      <div 
        key={control.access_id} 
        className={`rounded-2xl shadow-xl border p-6 ${
          isMaster 
            ? 'bg-blue-50 border-blue-200' 
            : 'bg-white border-orange-200'
        }`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            {isMaster ? (
              <Crown className="h-6 w-6 text-blue-600" />
            ) : (
              <UserCog className="h-6 w-6 text-orange-600" />
            )}
            <div>
              <h4 className={`text-lg font-semibold ${isMaster ? 'text-blue-800' : 'text-gray-800'}`}>
                {displayName}
              </h4>
              <p className={`text-sm ${isMaster ? 'text-blue-600' : 'text-gray-600'}`}>
                {isMaster ? 'Control maestro del negocio' : `ID: ${control.admin_id}`}
              </p>
            </div>
          </div>
          {isMaster && (
            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
              MAESTRO
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(accessConfig).map(([accessType, { label, icon }]) => {
            const isEnabled = control[accessType];
            const toggleKey = isMaster ? `master-${accessType}` : `${control.admin_id}-${accessType}`;
            const isLoading = isToggling === toggleKey;
            
            return (
              <div 
                key={accessType} 
                className={`border rounded-lg p-4 transition-colors ${
                  isMaster 
                    ? 'border-blue-200 hover:bg-blue-100' 
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${
                      isEnabled 
                        ? (isMaster ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600')
                        : 'bg-gray-100 text-gray-400'
                    }`}>
                      {icon}
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-800">{label}</h5>
                      <p className="text-sm text-gray-500">
                        {isEnabled ? 'Habilitado' : 'Deshabilitado'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {isEnabled ? 'Activo' : 'Inactivo'}
                  </span>
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
                  ) : (
                    <ToggleSwitch 
                      isEnabled={isEnabled} 
                      onChange={() => handleAccessToggle(control, accessType, isEnabled)} 
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gradient-to-br from-orange-50 to-orange-100 min-h-screen">
      {/* Sub-header with Registry Number Input */}
      <div className="bg-white border-b border-orange-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <form onSubmit={handleSearch} className="flex gap-4 max-w-2xl">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building className="h-5 w-5 text-orange-400" />
              </div>
              <input
                type="text"
                placeholder="Ingrese el número de registro del negocio..."
                value={registryNumber}
                onChange={(e) => setRegistryNumber(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 hover:border-orange-300 transition-all duration-200"
              />
            </div>
            <button
              type="submit"
              disabled={isSearching || !registryNumber.trim()}
              className="px-6 py-2 bg-gradient-to-r from-black to-gray-800 hover:from-gray-800 hover:to-black text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isSearching ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
            </button>
          </form>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {searchError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <div className="flex items-center">
              <AlertTriangle className="h-6 w-6 text-red-500 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-red-800">NEGOCIO NO ENCONTRADO</h3>
                <p className="text-red-600 mt-1">
                  {searchError.includes('not found') 
                    ? `El número de registro "${registryNumber}" no se encontró.` 
                    : searchError}
                </p>
              </div>
            </div>
          </div>
        )}

        {businessData && (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-xl border border-orange-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                    <Building className="h-6 w-6 text-orange-500 mr-3" />
                    {businessData.business_name}
                  </h2>
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center">
                      <User className="h-5 w-5 text-orange-500 mr-3" />
                      <span className="text-gray-700 font-medium">{businessData.business_owner}</span>
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    isMasterUser 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-orange-100 text-orange-800'
                  }`}>
                    {isMasterUser ? (
                      <>
                        <Crown className="h-4 w-4 mr-1" />
                        Usuario Maestro
                      </>
                    ) : (
                      <>
                        <UserCog className="h-4 w-4 mr-1" />
                        Usuario Admin
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {masterAccess.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold text-blue-700 mb-6 flex items-center">
                  <Crown className="h-6 w-6 mr-2" />
                  Control de Acceso Maestro
                </h3>
                <div className="space-y-6">
                  {masterAccess.map(control => renderAccessControlCard(control, 'master'))}
                </div>
              </div>
            )}

            {adminAccess.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                  <UserCog className="h-6 w-6 mr-2" />
                  Controles de Acceso por Administrador ({adminAccess.length})
                </h3>
                <div className="space-y-6">
                  {adminAccess.map(control => renderAccessControlCard(control, 'admin'))}
                </div>
              </div>
            )}

            {accessControls.length === 0 && (
              <div className="bg-white rounded-2xl shadow-xl border-2 border-dashed border-orange-200 p-12 text-center">
                <Users className="h-12 w-12 text-orange-300 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-700">Sin Controles de Acceso</h4>
                <p className="text-gray-500 mt-2 mb-6">
                  No se han asignado permisos para este negocio. Puede crear un conjunto de permisos por defecto.
                </p>
                
                {!showCreateOptions ? (
                  <button
                    onClick={() => setShowCreateOptions(true)}
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-lg shadow-md hover:shadow-lg hover:to-orange-700 transition-all duration-300"
                  >
                    <PlusCircle className="h-5 w-5 mr-2" />
                    Crear Acceso por Defecto
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <button
                        onClick={handleCreateDefaultAccess}
                        disabled={isCreatingDefault}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-lg shadow-md hover:shadow-lg hover:to-orange-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isCreatingDefault ? (
                          <>
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                            Creando...
                          </>
                        ) : (
                          <>
                            <UserCog className="h-5 w-5 mr-2" />
                            Crear Acceso para Mí ({isAdminUser ? 'Admin' : 'Usuario'})
                          </>
                        )}
                      </button>
                      
                      {isMasterUser && (
                        <button
                          onClick={handleCreateMasterAccess}
                          disabled={isCreatingMaster}
                          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold rounded-lg shadow-md hover:shadow-lg hover:to-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isCreatingMaster ? (
                            <>
                              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                              Creando...
                            </>
                          ) : (
                            <>
                              <Crown className="h-5 w-5 mr-2" />
                              Crear Acceso Maestro
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    
                    <button
                      onClick={() => setShowCreateOptions(false)}
                      className="text-gray-500 hover:text-gray-700 text-sm transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>
            )}

            {accessControls.length > 0 && (
              <div className="flex justify-center space-x-4">
                {(!adminAccess.some(a => a.admin_id === 'current_user') && isAdminUser) && (
                  <button
                    onClick={handleCreateDefaultAccess}
                    disabled={isCreatingDefault}
                    className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
                  >
                    {isCreatingDefault ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <UserCog className="h-4 w-4 mr-2" />
                    )}
                    Agregar Mi Acceso Admin
                  </button>
                )}
                
                {(masterAccess.length === 0 && isMasterUser) && (
                  <button
                    onClick={handleCreateMasterAccess}
                    disabled={isCreatingMaster}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isCreatingMaster ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Crown className="h-4 w-4 mr-2" />
                    )}
                    Crear Acceso Maestro
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {!businessData && !searchError && (
          <div className="text-center py-16">
            <Globe className="h-16 w-16 text-orange-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-700 mb-2">Gestión de Acceso</h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Ingrese el número de registro de un negocio para gestionar los permisos de acceso de los administradores y maestros.
            </p>
          </div>
        )}
      </main>

      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-red-600 flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2" />
                Confirmar Desactivación
              </h3>
              <button 
                onClick={cancelAccessChange} 
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-700">
                ¿Está seguro que desea desactivar el acceso a{' '}
                <strong>{pendingChange?.accessName}</strong> para{' '}
                <strong>{pendingChange?.userDisplayName}</strong>? 
                Esta acción será registrada.
              </p>
            </div>
            <div className="flex space-x-3">
              <button 
                onClick={cancelAccessChange} 
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={confirmAccessChange} 
                className="flex-1 px-4 py-2 text-white rounded-lg bg-red-600 hover:bg-red-700 transition-colors"
              >
                Confirmar Desactivación
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}